import React, { useState, useRef, useEffect } from 'react';

const Dropdown = ({
  label,
  options = [],
  value,
  onChange,
  placeholder = 'Select an option',
  disabled = false,
  required = false,
  error,
  multiple = false,
  searchable = false,
  className = '',
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  const filteredOptions = searchable
    ? options.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = option => {
    if (multiple) {
      const newValue = Array.isArray(value) ? value : [];
      const isSelected = newValue.some(v => v.value === option.value);
      onChange({
        target: {
          value: isSelected
            ? newValue.filter(v => v.value !== option.value)
            : [...newValue, option],
        },
      });
    } else {
      onChange({ target: { value: option } });
      setIsOpen(false);
    }
    setSearchTerm('');
  };

  const displayValue = () => {
    if (multiple && Array.isArray(value)) {
      return value.length > 0
        ? `${value.length} selected`
        : placeholder;
    }
    return value?.label || placeholder;
  };

  return (
    <div className={`mb-4 ${className}`} ref={dropdownRef}>
      {label && (
        <label className="label">
          {label}
          {required && <span className="text-danger ml-1">*</span>}
        </label>
      )}
      <div className="relative">
        <button
          type="button"
          className={`w-full px-3 py-2 border rounded-md text-sm text-left flex justify-between items-center focus:outline-none focus:ring-1 transition-colors ${
            error
              ? 'border-danger focus:border-danger focus:ring-danger'
              : 'border-gray-300 focus:border-primary focus:ring-primary'
          } ${disabled ? 'bg-gray-100 cursor-not-allowed text-gray-500' : 'bg-white hover:border-primary'}`}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
        >
          <span className="truncate">{displayValue()}</span>
          <span className={`transform transition-transform ${isOpen ? 'rotate-180' : ''}`}>
            ▼
          </span>
        </button>

        {isOpen && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50">
            {searchable && (
              <input
                type="text"
                placeholder="Search..."
                className="w-full px-3 py-2 border-b border-gray-200 text-sm focus:outline-none"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            )}
            <div className="max-h-48 overflow-y-auto">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option, index) => (
                  <button
                    key={index}
                    type="button"
                    className={`w-full px-3 py-2 text-sm text-left flex items-center gap-2 hover:bg-gray-100 transition-colors ${
                      multiple
                        ? Array.isArray(value) &&
                          value.some(v => v.value === option.value)
                          ? 'bg-blue-50 text-primary font-medium'
                          : ''
                        : value?.value === option.value
                        ? 'bg-blue-50 text-primary font-medium'
                        : ''
                    }`}
                    onClick={() => handleSelect(option)}
                  >
                    {multiple && (
                      <input
                        type="checkbox"
                        checked={
                          Array.isArray(value) &&
                          value.some(v => v.value === option.value)
                        }
                        readOnly
                        className="cursor-pointer"
                      />
                    )}
                    {option.label}
                  </button>
                ))
              ) : (
                <div className="px-3 py-2 text-sm text-gray-500 text-center">
                  No options found
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {error && <span className="block text-sm text-danger mt-1">{error}</span>}
    </div>
  );
};

export default Dropdown;
